import React, { useEffect, useState } from 'react';
import api from '../services/api';
import Modal from '../components/Modal';
import toast from 'react-hot-toast';
import { Plus, Edit2, Trash2, Check } from 'lucide-react';
import { formatCurrency, formatDate, canModifyRecord, isAdminUser, approvalBadge } from '../utils/helpers';

const empty = { vehicle_no:'', vehicle_type:'', model:'', capacity:'', driver_name:'', driver_phone:'', driver_license:'', insurance_expiry:'', fitness_expiry:'', permit_expiry:'', pollution_expiry:'', purchase_date:'', purchase_cost:0, current_km:0, status:'active' };

export default function Vehicles() {
  const [list, setList] = useState([]);
  const [filterApproval, setFilterApproval] = useState('all');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const admin = isAdminUser();

  const load = () => api.get('/vehicles').then(r => setList(r.data.data || []));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.vehicle_no) return toast.error('Vehicle number required');
    try {
      const r = editId ? await api.put('/vehicles/' + editId, form) : await api.post('/vehicles', form);
      toast.success(r.data.message || 'Saved'); setOpen(false); setForm(empty); setEditId(null); load();
    } catch (e) { toast.error(e?.response?.data?.message || 'Error'); }
  };
  const del = async (id) => { if (!window.confirm('Delete?')) return; try { await api.delete('/vehicles/' + id); toast.success('Deleted'); load(); } catch (e) { toast.error(e?.response?.data?.message || 'Error'); } };
  const approve = async (id) => { if (!window.confirm('Approve this vehicle?')) return; try { await api.post('/vehicles/' + id); toast.success('Approved'); load(); } catch (e) { toast.error(e?.response?.data?.message || 'Error'); } };
  const edit = (v) => { setForm({...empty, ...v}); setEditId(v.id); setOpen(true); };

  const filtered = list.filter(v => filterApproval === 'all' || v.approval_status === filterApproval);
  const pendingCount = list.filter(v => v.approval_status === 'pending').length;

  return (
    <div className="card">
      <div className="card-header">
        <h3>Vehicles / Fleet ({filtered.length}){pendingCount > 0 && <span className="badge badge-orange" style={{marginLeft:8,fontSize:11}}>{pendingCount} pending</span>}</h3>
        <button className="btn btn-blue" onClick={() => { setForm(empty); setEditId(null); setOpen(true); }}><Plus size={14}/> Add Vehicle</button>
      </div>
      <div className="search-bar" style={{display:'flex',gap:10,alignItems:'center'}}>
        <select value={filterApproval} onChange={e=>setFilterApproval(e.target.value)} style={{maxWidth:160}}>
          <option value="all">All Status</option>
          <option value="pending">Pending Only</option>
          <option value="approved">Approved Only</option>
        </select>
      </div>
      <div className="table-wrap">
        <table>
          <thead><tr><th>Vehicle No</th><th>Type</th><th>Driver</th><th>Phone</th><th>KM</th><th>Insurance</th><th>Fitness</th><th>Status</th><th>Approval</th><th>Actions</th></tr></thead>
          <tbody>
            {filtered.length ? filtered.map(v => {
              const badge = approvalBadge(v.approval_status);
              const canMod = canModifyRecord(v);
              const isPending = v.approval_status === 'pending';
              return (
                <tr key={v.id} style={isPending ? {background:'#fffbeb'} : {}}>
                  <td><strong>{v.vehicle_no}</strong></td>
                  <td>{v.vehicle_type || '-'}</td>
                  <td>{v.driver_name || '-'}</td>
                  <td>{v.driver_phone || '-'}</td>
                  <td>{v.current_km}</td>
                  <td>{formatDate(v.insurance_expiry)}</td>
                  <td>{formatDate(v.fitness_expiry)}</td>
                  <td><span className={'badge ' + (v.status==='active'?'badge-green':v.status==='maintenance'?'badge-orange':'badge-red')}>{v.status}</span></td>
                  <td>
                    <span className={badge.cls}>{badge.label}</span>
                    {v.created_by_name && <small style={{display:'block',color:'#94a3b8',fontSize:10,marginTop:2}}>by {v.created_by_name}</small>}
                  </td>
                  <td>
                    {admin && isPending && <button className="btn btn-sm btn-green" onClick={()=>approve(v.id)} title="Approve"><Check size={12}/></button>}
                    {' '}
                    {canMod && <button className="btn btn-sm btn-gray" onClick={()=>edit(v)} title="Edit"><Edit2 size={12}/></button>}
                    {' '}
                    {canMod && <button className="btn btn-sm btn-red" onClick={()=>del(v.id)} title="Delete"><Trash2 size={12}/></button>}
                    {!canMod && !isPending && <span style={{color:'#94a3b8',fontSize:11}}>Locked</span>}
                  </td>
                </tr>
              );
            }) : <tr><td colSpan="10" className="empty">No vehicles</td></tr>}
          </tbody>
        </table>
      </div>

      <Modal open={open} onClose={()=>setOpen(false)} title={editId?'Edit Vehicle':'Add Vehicle'} size="lg"
        footer={<>
          <button className="btn btn-gray" onClick={()=>setOpen(false)}>Cancel</button>
          <button className="btn btn-blue" onClick={save}>{editId?'Update':'Create'}</button>
        </>}>
        {!editId && !admin && (
          <div style={{background:'#fef3c7',border:'1px solid #fcd34d',padding:'10px 12px',borderRadius:8,marginBottom:14,fontSize:13,color:'#92400e'}}>
            Your entry will be saved as <strong>pending</strong> until an admin approves it.
          </div>
        )}
        <div className="form-grid">
          <div className="form-group"><label>Vehicle No *</label><input value={form.vehicle_no} onChange={e=>setForm({...form, vehicle_no:e.target.value.toUpperCase()})}/></div>
          <div className="form-group"><label>Vehicle Type</label>
            <select value={form.vehicle_type} onChange={e=>setForm({...form, vehicle_type:e.target.value})}>
              <option value="">-- Select --</option>
              <option>Truck</option><option>Mini Truck</option><option>Lorry</option><option>Tempo</option><option>Pickup</option><option>Container</option><option>Other</option>
            </select>
          </div>
          <div className="form-group"><label>Model</label><input value={form.model} onChange={e=>setForm({...form, model:e.target.value})}/></div>
          <div className="form-group"><label>Capacity</label><input placeholder="e.g. 10 Ton" value={form.capacity} onChange={e=>setForm({...form, capacity:e.target.value})}/></div>
          <div className="form-group"><label>Driver Name</label><input value={form.driver_name} onChange={e=>setForm({...form, driver_name:e.target.value})}/></div>
          <div className="form-group"><label>Driver Phone</label><input value={form.driver_phone} onChange={e=>setForm({...form, driver_phone:e.target.value})}/></div>
          <div className="form-group"><label>Driver License</label><input value={form.driver_license} onChange={e=>setForm({...form, driver_license:e.target.value})}/></div>
          <div className="form-group"><label>Current Odometer (KM)</label><input type="number" value={form.current_km} onChange={e=>setForm({...form, current_km:e.target.value})}/></div>
          <div className="form-group"><label>Insurance Expiry</label><input type="date" value={form.insurance_expiry||''} onChange={e=>setForm({...form, insurance_expiry:e.target.value})}/></div>
          <div className="form-group"><label>Fitness Expiry</label><input type="date" value={form.fitness_expiry||''} onChange={e=>setForm({...form, fitness_expiry:e.target.value})}/></div>
          <div className="form-group"><label>Permit Expiry</label><input type="date" value={form.permit_expiry||''} onChange={e=>setForm({...form, permit_expiry:e.target.value})}/></div>
          <div className="form-group"><label>Pollution Expiry</label><input type="date" value={form.pollution_expiry||''} onChange={e=>setForm({...form, pollution_expiry:e.target.value})}/></div>
          <div className="form-group"><label>Purchase Date</label><input type="date" value={form.purchase_date||''} onChange={e=>setForm({...form, purchase_date:e.target.value})}/></div>
          <div className="form-group"><label>Purchase Cost</label><input type="number" value={form.purchase_cost} onChange={e=>setForm({...form, purchase_cost:e.target.value})}/></div>
          <div className="form-group"><label>Status</label>
            <select value={form.status} onChange={e=>setForm({...form, status:e.target.value})}>
              <option value="active">Active</option><option value="maintenance">In Maintenance</option><option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
      </Modal>
    </div>
  );
}
